/**
 * SOLO ROLL (solo_roll) — the player throws. Mirrors the craps ComeOutState
 * roll setup, minus the shooter-handoff (solo is always the same player). The
 * server authorizes the local player (2 dice for Hi-Lo, 3 for Chuck-a-Luck via
 * getExpectedDiceCount) and settles in onPhaseExit, so the dice-result flyout
 * and win payout ride the exit broadcast.
 *
 * Hi-Lo 7 continue (ROLL → REROLL → ROLL, the PointRollState pattern):
 *   - onExit to REROLL → no settlement happened; skip the resolution chain,
 *     dice stay where they landed.
 *   - onEnter from REROLL → skip the betting-exit/camera setup; just
 *     re-authorize the same player so they pick the dice back up.
 */

import type { GameUpdate } from '@sdl/shared';
import { SoloState } from '@sdl/shared';
import { State } from '../State.js';

export class SoloRollState extends State {
  async onEnter(update: GameUpdate): Promise<void> {
    const game = this.game;

    this._resetRollSetup();

    if (update.previousState === SoloState.REROLL) {
      // Continue re-entry: the 7 settled nothing. Dice are already in scene
      // (setup re-enables pickup, no re-spawn); the bet and the house cover
      // are still on the table. Tell the player why nothing paid.
      game.instructionBanner?.show(
        { title: 'Seven!', text: 'No action. Pick the dice up and roll again' },
        update.animated,
      );
      this._updateProfiles(update);
      await this._checkPlayers(update);
      this._maybeSetupLocalRoll(update);
      return;
    }

    game.playerProfilesOverlay?.show();
    game.playerProfilesOverlay?.setMode('shooting');
    game.bettingHUD3D?.setLockInVisible(false);
    game.countdownOverlay?.stop();
    game.instructionBanner?.hide();

    await game.exitBettingMode({
      animated: update.animated,
      previousState: update.previousState,
    });

    this._updateProfiles(update);
    await this._checkPlayers(update);

    // Roll authorization is STATE (update.rollAuthorizedFor). Fires the local
    // roll setup from whichever hook first sees us authorized.
    this._maybeSetupLocalRoll(update);
  }

  async onUpdate(update: GameUpdate): Promise<void> {
    this._updateProfiles(update);
    await this._checkPlayers(update);
    this._maybeSetupLocalRoll(update);
  }

  async onExit(update: GameUpdate): Promise<void> {
    // Continue loop (Hi-Lo 7): nothing settled — skip the resolution chain
    // entirely so the dice stay on the floor for the re-pickup.
    if (update.gameState === SoloState.REROLL) return;
    // Pure resolution choreography: dice-result flyout + win payout ride the
    // exit broadcast. No shooter handoff (solo), no next-round zone to prime
    // (BETTING creates its own bet zone on the player's next drop).
    await this._checkPlayers(update);
    await this.game.playRollResolution(update);
  }
}
